import type {
	BaseSuggestionHandlerConfig,
	GetLocationFn
} from './shared/types';
import type { Suggestion } from '~/components/SearchSuggest/types';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';

import {
	createHandlerMapBuilder,
	createStrategyHandler
} from './shared/baseStrategyHandler';
import {
	closeIfNeeded,
	createLocationEvent,
	fetchAndUpdateLocation,
	getTextFromParams,
	isHistoryItem,
	needsLocationFetch
} from './shared/utils';

/**
 * Configuration for first field suggestion handler
 * Extends base config with first-field specific requirements
 */
interface FirstFieldSuggestionHandlerConfig extends BaseSuggestionHandlerConfig {
	onEmitHistory: (suggestion: Suggestion, isEmptyState: boolean) => void;
	onEmitCode: (searchParams: Suggestion['searchParams']) => void;
	onUpdateSelectedLocation: (location: SelectAddressEvent) => void;
	onSetHasSelectedValue: (value: boolean) => void;
	getDisplayValue: (suggestion: Suggestion) => string;
	shouldShowEmptyState: boolean;
}

/**
 * Core composable for handling first field suggestion selection with Strategy Pattern
 *
 * Refactored to use shared utilities following DRY, KISS, and SOLID principles:
 * - DRY: Shared logic extracted to utilities
 * - KISS: Each handler has a single, clear purpose
 * - Single Responsibility: Each handler focuses on one suggestion type
 * - Open/Closed: Easy to add new suggestion types without modifying existing code
 * - Dependency Inversion: Depends on abstractions (handlers) not concrete implementations
 */
export function useFirstFieldSuggestionHandler(getLocation: GetLocationFn) {
	const { selectedMacroregion } = useTwoFieldSearchSuggestState();

	/**
	 * Handler for history items
	 * Uses shared utilities for DRY compliance
	 */
	const handleHistory = async (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		const isPoiOrStreet = ['point_of_interest', 'street'].includes(
			suggestion.searchType
		);

		if (isPoiOrStreet) {
			if (!selectedMacroregion.value && suggestion.macroregionUsed) {
				selectedMacroregion.value = suggestion.macroregionUsed;
			}
		}

		// Get text from params or display value
		const text = getTextFromParams(suggestion, config.getDisplayValue);
		if (text) {
			config.searchTerm.value = text;
		}

		config.onSetHasSelectedValue(true);

		// For street and POI history items, fetch coordinates if missing
		if (needsLocationFetch(suggestion) && text) {
			const updatedSuggestion = await fetchAndUpdateLocation(
				suggestion,
				text,
				getLocation
			);
			config.onUpdateResearch(updatedSuggestion);
			config.onEmitHistory(updatedSuggestion, config.shouldShowEmptyState);
		} else {
			config.onUpdateResearch(suggestion);
			config.onEmitHistory(suggestion, config.shouldShowEmptyState);
		}

		closeIfNeeded(config);
	};

	/**
	 * Handler for code suggestions
	 */
	const handleCode = (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		config.onUpdateResearch(suggestion);
		config.onEmitCode(suggestion.searchParams);
		config.onSetHasSelectedValue(true);
		closeIfNeeded(config);
	};

	/**
	 * Handler for region/macroregion/city selections
	 */
	const handleLocation = (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		// If it's a history item, emit history event
		if (isHistoryItem(suggestion)) {
			const displayText = getTextFromParams(suggestion, config.getDisplayValue);
			config.searchTerm.value = displayText;
			config.onSetHasSelectedValue(true);
			config.onUpdateResearch(suggestion);
			config.onEmitHistory(suggestion, config.shouldShowEmptyState);
			closeIfNeeded(config);
			return;
		}

		// New selection: update selectedLocation
		const displayText = config.getDisplayValue(suggestion);
		const locationEvent = createLocationEvent(suggestion, displayText);

		config.onUpdateSelectedLocation(locationEvent);
		config.searchTerm.value = displayText;
		config.onSetHasSelectedValue(true);
		config.onClose(); // Always close for location selections
	};

	/**
	 * Handler for street selections
	 */
	const handleStreet = async (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		if (!selectedMacroregion.value && suggestion.macroregionUsed) {
			selectedMacroregion.value = suggestion.macroregionUsed;
		}

		// If it's a history item, emit history event
		if (isHistoryItem(suggestion)) {
			const displayText = getTextFromParams(suggestion, config.getDisplayValue);
			config.searchTerm.value = displayText;
			config.onSetHasSelectedValue(true);
			config.onUpdateResearch(suggestion);
			config.onEmitHistory(suggestion, config.shouldShowEmptyState);
			closeIfNeeded(config);
			return;
		}

		// New selection: get coordinates and update
		const displayText = config.getDisplayValue(suggestion);
		const { lat, lng } = await getLocation(
			suggestion.searchParams.placeId ?? null
		);

		const locationEvent = createLocationEvent(suggestion, displayText, {
			lat: lat ?? undefined,
			lng: lng ?? undefined,
			macroregions: [],
			cities: [],
			regions: []
		});

		config.onUpdateSelectedLocation(locationEvent);
		config.searchTerm.value = displayText;
		config.onSetHasSelectedValue(true);
		config.onClose(); // Always close for street selections
	};

	/**
	 * Handler for point of interest selections
	 */
	const handlePoi = async (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		// If it's a history item, emit history event

		if (!selectedMacroregion.value && suggestion.macroregionUsed) {
			selectedMacroregion.value = suggestion.macroregionUsed;
		}

		if (isHistoryItem(suggestion)) {
			const displayText = getTextFromParams(suggestion, config.getDisplayValue);
			config.searchTerm.value = displayText;
			config.onSetHasSelectedValue(true);
			config.onUpdateResearch(suggestion);
			config.onEmitHistory(suggestion, config.shouldShowEmptyState);
			closeIfNeeded(config);
			return;
		}

		// New selection: get coordinates and update
		const displayText = config.getDisplayValue(suggestion);
		const { lat, lng } = await getLocation(
			suggestion.searchParams.placeId ?? null
		);

		const locationEvent = createLocationEvent(suggestion, displayText, {
			lat: lat ?? undefined,
			lng: lng ?? undefined,
			macroregions: [],
			cities: [],
			regions: []
		});

		config.onUpdateSelectedLocation(locationEvent);
		config.searchTerm.value = displayText;
		config.onSetHasSelectedValue(true);
		config.onClose(); // Always close for POI selections
	};

	/**
	 * Handler for other history items (fallback)
	 */
	const handleOtherHistory = (
		suggestion: Suggestion,
		config: FirstFieldSuggestionHandlerConfig
	) => {
		config.onEmitHistory(suggestion, false);
		closeIfNeeded(config);
	};

	/**
	 * Build strategy map using handler builder pattern
	 * Makes it clear and maintainable to see which types use which handlers
	 */
	const handlerMap =
		createHandlerMapBuilder<FirstFieldSuggestionHandlerConfig>()
			.register('research', handleHistory)
			.register('code', handleCode)
			.registerMultiple(['region', 'macroregion', 'city'], handleLocation)
			.register('street', handleStreet)
			.register('point_of_interest', handlePoi)
			.build();

	/**
	 * Create the main handler using shared strategy pattern implementation
	 * This eliminates duplication of the delegation logic
	 */
	return createStrategyHandler(handlerMap, handleOtherHistory);
}
